import { logEvent, Analytics } from 'firebase/analytics';
import { logger } from '@/utils/logger';
// We'll import analytics synchronously to avoid all the async complexity
import { analytics } from '@/firebase';
import { isInternalTraffic } from '@/utils/internalTraffic';
import { isMobileIOS } from '@/utils/browserEnv';
import type { AppStorePlacement } from '@/config/appStoreLink';

export interface AnalyticsEventParams {
  quiz_started: {
    quiz_id: number;
    quiz_type: string;
    category?: string;
  };
  quiz_completed: {
    quiz_id: number;
    score: number;
    totalQuestions: number;
    percentage: number;
    timeSpent: number;
    questionIds: number[];
  };
  question_answered: {
    questionId: number;
    isCorrect: boolean;
    timeSpent: number;
    attemptCount: number;
    hintUsed: boolean;
    // Exam domain + difficulty so GA4 can segment answer-engagement by area.
    category?: string;
    difficulty?: number;
    // NEW: Phase 2 enhanced metadata fields
    confidence?: number;              // User confidence level (1-5 scale)
    flaggedForReview?: boolean;       // Question marked for later review
    hintsUsedCount?: number;          // Total hints used on this question
    timeSpentReading?: number;        // Time spent reading before answering
  };
  
  question_interaction_detailed: {
    questionId: string;
    isCorrect: boolean;
    timeSpent: number;
    category: string;
    difficulty: number;
    hintUsed: boolean;
    attemptOrder: number;
    distractorChosen?: string;
  };
  
  answer_changed_detailed: {
    sessionId: string;
    questionId: string;
    oldAnswer: string;
    newAnswer: string;
    timestamp: string;
  };
  
  hint_used_detailed: {
    sessionId: string;
    questionId: string;
    hintType: string;
    timestamp: string;
  };
  
  question_timing_detailed: {
    questionId: string;
    startTime: string;
    endTime: string;
    totalTime: number;
    thinkingTime: number;
    pageVisible: boolean;
  };
  
  distractor_analysis: {
    questionId: string;
    correctAnswer: string;
    selectedAnswer: string;
    distractorType: string;
    isPlausible: boolean;
  };
  // Generic error event (for backwards compatibility)
  error_occurred: {
    error_type: string;
    error_message: string;
    stack_trace?: string;
    component?: string;
    quiz_id?: number;
    question_id?: number;
    userId?: string;
    timestamp?: number;
    time_taken?: number;
    impact_level?: 'critical' | 'high' | 'medium' | 'low';
    error_category?: string;
    user_journey_impact?: string;
    specific_error_type?: string;
    [key: string]: string | number | boolean | undefined;
  };
  
  // 🔥 CRITICAL: Blocks core user journeys
  quiz_load_failed: {
    error_message: string;
    component?: string;
    action?: string;
    quiz_id?: string;
    user_id?: string;
    impact_level: 'critical';
    timestamp: string;
  };
  
  question_submit_failed: {
    error_message: string;
    question_id?: string;
    quiz_id?: string;
    user_id?: string;
    impact_level: 'critical';
    timestamp: string;
  };
  
  payment_failed: {
    error_message: string;
    payment_method?: string;
    plan_type?: string;
    user_id?: string;
    impact_level: 'critical';
    timestamp: string;
  };
  
  signin_failed: {
    error_message: string;
    auth_provider?: string;
    user_id?: string;
    impact_level: 'critical';
    timestamp: string;
  };
  
  // 📚 HIGH: Feature-specific errors that impact learning
  flashcard_load_failed: {
    error_message: string;
    deck_id?: string;
    user_id?: string;
    impact_level: 'high';
    timestamp: string;
  };
  
  progress_sync_failed: {
    error_message: string;
    data_type?: string;
    user_id?: string;
    impact_level: 'high';
    timestamp: string;
  };
  
  content_load_failed: {
    error_message: string;
    content_type?: string;
    user_id?: string;
    impact_level: 'high';
    timestamp: string;
  };
  
  // 🚀 MEDIUM: Performance issues
  quiz_error: {
    error_message: string;
    quiz_id?: string;
    user_id?: string;
    impact_level: 'medium';
    timestamp: string;
  };
  
  flashcard_error: {
    error_message: string;
    deck_id?: string;
    user_id?: string;
    impact_level: 'medium';
    timestamp: string;
  };
  
  api_timeout: {
    error_message: string;
    endpoint?: string;
    timeout_duration?: number;
    impact_level: 'medium';
    timestamp: string;
  };
  
  page_load_timeout: {
    error_message: string;
    page_name?: string;
    load_time?: number;
    impact_level: 'medium';
    timestamp: string;
  };
  
  memory_error: {
    error_message: string;
    heap_size?: number;
    impact_level: 'medium';
    timestamp: string;
  };
  
  // 🔧 LOW: Technical errors (existing categories)
  api_error: {
    error_message: string;
    endpoint?: string;
    status_code?: number;
    impact_level: 'low';
    timestamp: string;
  };
  
  network_error: {
    error_message: string;
    network_type?: string;
    impact_level: 'low';
    timestamp: string;
  };
  
  js_error: {
    error_message: string;
    stack_trace?: string;
    filename?: string;
    line_number?: number;
    impact_level: 'low';
    timestamp: string;
  };
  page_viewed: {
    page_name: string;
    is_authenticated: boolean;
    timestamp: string;
    referrer?: string;
    user_id?: string;
    mode?: string;
    time_on_page?: number;
    viewport?: {
      width: number;
      height: number;
    };
    loadTime?: number;
    acquisition_channel?: string;
    utm_source?: string;
    utm_campaign?: string;
  };
  landing_page_viewed: {
    page_name: string;
    variant?: string;
    exam_type?: string;
    acquisition_channel?: string;
    utm_source?: string;
    utm_campaign?: string;
    referrer?: string;
    timestamp: string;
  };
  trial_signup_started: {
    signup_method: string;
    variant?: string;
    user_segment?: string;
    acquisition_channel?: string;
    exam_type?: string;
    timestamp?: string;
  };
  // Per-screen progression inside the account-last /onboarding flow, so the funnel
  // no longer goes dark between email capture and trial provisioning (spec P3).
  onboarding_step_viewed: {
    step: string;
    step_index: number;
    exam_type?: string;
    variant?: string;
    timestamp?: string;
    // Licensing step (#411) — same event, extra params, so the existing funnel
    // query keeps working and `step:"licensing_pathway"` is just another value.
    jurisdiction?: string;
    rung?: string;
    stage?: string;
    employment?: string;
    exam_required?: boolean;
  };
  onboarding_step_completed: {
    step: string;
    step_index?: number;
    exam_type?: string;
    jurisdiction?: string;
    rung?: string;
    stage?: string;
    employment?: string;
    exam_required?: boolean;
    timestamp?: string;
  };
  /**
   * The "Your path in <State>" dashboard card rendered with real data (#411).
   * Fires once per mount. Deliberately NOT a key event: the three Google Ads
   * conversions (diagnostic_started, diagnostic_completed, purchase) are
   * untouched by this feature.
   */
  licensing_card_viewed: {
    jurisdiction: string;
    rung: string;
    exam_required: boolean;
    headline_kind?: string;
  };
  trial_signup_completed: {
    signup_method: string;
    user_segment?: string;
    acquisition_channel?: string;
    exam_type?: string;
    timestamp?: string;
  };
  trial_activated: {
    user_id: string;
    user_segment?: string;
    days_since_signup: number;
    timestamp: string;
  };
  trial_converted: {
    user_id: string;
    user_segment?: string;
    acquisition_channel?: string;
    days_since_signup: number;
    trial_duration: number;
    timestamp: string;
  };
  sign_up_started: {
    signup_method: string;
    user_segment?: string;
    acquisition_channel?: string;
    timestamp?: string;
  };
  // Fired when a signup attempt fails (e.g. social popup blocked in an in-app
  // browser, or an email-signup error). Makes the silent signup leak VISIBLE in
  // GA4 — turns "0 completed, no idea why" into a counted failure reason.
  sign_up_failed: {
    signup_method: string;
    reason: string;        // firebase error code or short reason
    in_app_browser?: boolean;
    timestamp?: string;
  };
  // Real-user round-trip latency of the core quiz callables (answerQuestion,
  // getNextQuestion, initializeSession). `value` is the latency in ms so GA4's
  // eventValue aggregates it (avg = eventValue/eventCount); `call` segments by
  // which one, `cold` flags likely cold starts. This is how we measure whether
  // questions/answers/next-questions load fast for actual users.
  quiz_latency: {
    call: string;          // 'answerQuestion' | 'getNextQuestion' | 'initializeSession'
    value: number;         // latency in ms (GA4 event value)
    ok: boolean;
    cold: boolean;
  };
  // Fired when a metered-free-tier user exhausts their daily question allowance
  // and the in-flow paywall renders. THE conversion-intent signal for the free
  // tier — appetite peaks at the cap, so this is what we optimize the $129
  // "until you pass" upsell against. `limit` is the daily cap, `resets_at_utc`
  // is when the allowance refreshes, `test_type`/`context` segment where it hit.
  free_cap_hit: {
    limit: number;
    test_type?: string;
    context?: string;      // 'submit_answer' | 'initialize_session'
    resets_at_utc?: string;
    timestamp?: string;
  };
  user_interaction: {
    interaction_type: string;
    segment?: string;
    timestamp?: string;
    [key: string]: string | number | undefined;
  };
  web_vital_recorded: {
    metric: string;
    value: number;
    rating: 'good' | 'needs-improvement' | 'poor';
    metricId: string;
    timestamp: string;
  };
  settings_update_profile: {
    userId: string;
    timestamp: number;
  };
  settings_manage_subscription: {
    userId: string;
    currentStatus: string;
    timestamp: number;
  };
  settings_change_password: {
    userId: string;
    timestamp: number;
  };
  settings_send_feedback: {
    userId: string;
    feedbackLength: number;
    timestamp: number;
  };
  settings_page_viewed: {
    userId: string;
    isSubscribed: boolean;
    timestamp: number;
  };
  delete_quiz: {
    quizId: string | number;
    success: boolean;
    error?: string;
  };
  subscription_step: {
    step: string;
    source?: string;
    status?: string;
    userId?: string;
    timestamp: number;
    timeOnPage?: number;
    isSubscribed?: boolean;
    loadTime?: number;
  };
  subscription_clicked: {
    subscriptionStatus: string | null;
    isLoggedIn: boolean;
    source?: string;
    timestamp?: number;
    timeOnPage?: number;
  };
  auth_started: {
    method: string;
    email_provided: boolean;
    has_referrer: boolean;
    timestamp: number;
  };
  auth_completed: {
    method: string;
    time_taken: number;
    has_redirect: boolean;
    redirect_path: string;
    timestamp: number;
  };
  auth_error: {
    method: string;
    error_type: string;
    error_message: string;
    time_taken: number;
    email_provided: boolean;
    timestamp: number;
  };
  auth_method_selected: {
    method: string;
    timestamp: number;
  };
  scroll_depth: {
    depth: number;
    timestamp: number;
    timeOnPage: number;
  };
  feature_usage: {
    feature: string;
    action: string;
    timestamp: number;
    timeOnPage: number;
  };
  settings_email_preference_updated: {
    userId: string;
    preferenceType: string;
    newValue: boolean;
    timestamp: number;
  };
  // Revenue and subscription tracking
  subscription_completed: {
    subscription_id: string;
    customer_id: string;
    plan_type: string;
    revenue_amount: number;
    currency: string;
    payment_method: string;
    conversion_source?: string;
    time_to_conversion?: number;
    trial_duration?: number;
    anonymous_quiz_results?: boolean;
    google_ads_attribution?: string;
  };
  google_ads_conversion: {
    conversion_action: string;
    conversion_value?: number;
    currency?: string;
    conversion_source?: string;
    anonymous_quiz_results?: boolean;
    time_to_conversion?: number;
    trial_duration?: number;
    google_ads_attribution?: string;
  };
  subscription_failed: {
    error_code?: string;
    error_message?: string;
    payment_method?: string;
    plan_type?: string;
    failure_reason?: string;
    retry_attempt?: number;
  };
  // Conversion funnel tracking
  anonymous_to_auth_conversion: {
    conversion_trigger: string;
    quiz_results_preserved: boolean;
    anonymous_session_duration: number;
    quiz_score_at_conversion?: number;
    quiz_questions_answered?: number;
  };
  trial_to_paid_conversion: {
    trial_duration_days: number;
    quiz_sessions_completed: number;
    final_quiz_score?: number;
    conversion_source: string;
  };
  // Email attribution
  email_conversion: {
    email_source: string;
    email_campaign: string;
    conversion_action: string;
    time_from_email_to_conversion: number;
    email_click_timestamp?: number;
  };
  // Business metrics
  subscription_cancelled: {
    subscription_id: string;
    cancellation_reason?: string;
    days_subscribed: number;
    total_revenue: number;
    quiz_sessions_completed: number;
    final_engagement_score?: number;
  };
  retention_milestone: {
    milestone_type: string; // '7_day', '30_day', '90_day', '1_year'
    user_cohort: string;
    quiz_sessions_in_period: number;
    engagement_score: number;
  };
  customer_ltv_updated: {
    customer_id: string;
    current_ltv: number;
    predicted_ltv: number;
    subscription_months: number;
    total_revenue: number;
  };
  quiz_engagement_metrics: {
    session_id: string;
    score: number;
    time_spent: number;
    questions_answered: number;
    hints_used: number;
    engagement_quality: number;
  };
  question_feedback_provided: {
    questionId: string;
    feedback: 'positive' | 'negative';
    sessionId: string;
    isCorrect: boolean;
    category: string;
    timeSpent?: number;
    userId?: string;
    timestamp: number;
  };
  flashcard_deck_created: {
    deckId: string;
    category: string;
    cardCount: number;
    avgDifficulty: number;
  };
  flashcard_session_started: {
    deckId: string;
    sessionId: string;
    totalCards: number;
  };
  flashcard_session_completed: {
    sessionId: string;
    correctAnswers: number;
    totalCards: number;
    accuracy: number;
    averageTime: number;
  };
  // Navigation Events
  navigation_click: {
    element_type: 'header_link' | 'footer_link' | 'mobile_menu' | 'logo' | 'social_media' | 'breadcrumb' | 'sidebar_link' | 'bottom_tab';
    element_name: string;
    destination_page: string;
    user_authenticated: boolean;
    timestamp: number;
    current_page?: string;
  };
  // Theme and Preference Events
  theme_preference_changed: {
    previous_theme: 'light' | 'dark';
    new_theme: 'light' | 'dark';
    user_id?: string;
    trigger_type: 'manual' | 'system';
    timestamp: number;
  };
  // Form Abandonment Events
  form_abandonment: {
    form_type: 'signin' | 'signup' | 'settings' | 'feedback' | 'profile';
    fields_completed: string[];
    fields_total: number;
    completion_percentage: number;
    time_on_form: number;
    abandonment_point: string;
    timestamp: number;
  };
  form_field_interaction: {
    form_type: string;
    field_name: string;
    interaction_type: 'focus' | 'blur' | 'change' | 'error' | 'clear';
    field_value_length?: number;
    validation_status?: 'valid' | 'invalid' | 'pending';
    timestamp: number;
  };
  // Enhanced Error Events
  page_not_found: {
    requested_url: string;
    referrer: string;
    user_authenticated: boolean;
    timestamp: number;
    user_agent?: string;
  };
  form_validation_error: {
    form_type: string;
    field_name: string;
    error_type: string;
    error_message: string;
    timestamp: number;
  };
  // Enhanced Engagement Events
  scroll_depth_detailed: {
    page_name: string;
    depth_percentage: number;
    depth_pixels: number;
    time_to_depth: number;
    max_depth_reached: number;
    timestamp: number;
  };
  engagement_milestone: {
    milestone_type: 'time_on_page' | 'scroll_depth' | 'interaction_count';
    milestone_value: number;
    page_name: string;
    session_duration: number;
    timestamp: number;
  };
  // Portal Token Authentication Events
  token_auth_started: {
    hasExpiry: boolean;
    hasSignature: boolean;
    timestamp: number;
  };
  token_auth_success: {
    userId: string;
    timestamp: number;
    platform: string;
  };
  token_auth_failed: {
    error: string;
    timestamp: number;
  };
  token_auth_skipped: {
    reason: string;
    userId?: string;
    timestamp: number;
  };
  // Exam Result Events
  exam_results_page_viewed: {
    exam_type: string;
    has_existing_result: boolean;
  };
  exam_result_selected: {
    result: 'passed' | 'failed';
    exam_type: string;
  };
  exam_result_submitted: {
    passed: boolean;
    has_score: boolean;
    has_feedback: boolean;
    exam_type: string;
  };
  // Clinical Simulation Events
  simulation_started: {
    scenario_id: string;
  };
  simulation_completed: {
    scenario_id: string;
    reason: string;
    duration: number;
    messages: number;
  };
  simulation_abandoned: {
    scenario_id: string;
    reason: string;
    duration: number;
    messages: number;
  };
  // Testimonial Events
  testimonial_page_viewed: {
    exam_type?: string;
  };
  testimonial_submitted: {
    exam_type: string;
    has_photo_consent: boolean;
    has_social_consent: boolean;
  };
  // Off-platform review prompt (Apple App Store / Google Business), shown
  // at positive moments like post-pass.
  review_prompt_shown: {
    context: string;
    google_available: boolean;
  };
  review_prompt_clicked: {
    context: string;
    target: 'apple_app_store' | 'google_business';
  };
  adaptive_algorithm_decision: Record<string, unknown>;
  [key: string]: Record<string, unknown>;
}

// Simple getter for analytics instance
const getAnalyticsInstance = (): Analytics | null => {
  return analytics;
};

// Define all analytics events as a const object or enum
export const ANALYTICS_EVENTS = {
  QUIZ_STARTED: 'quiz_started' as const,
  QUIZ_COMPLETED: 'quiz_completed' as const,
  QUIZ_START_ERROR: 'quiz_start_error' as const,
  QUESTION_ANSWERED: 'question_answered' as const,
  PROFILE_VIEWED: 'profile_viewed',
  RETAKE_QUIZ: 'retake_quiz',
  CONTINUE_SAVED_QUIZ: 'continue_saved_quiz',
  DELETE_QUIZ: 'delete_quiz' as const,
  ONBOARDING_STARTED: 'onboarding_started',
  ONBOARDING_COMPLETED: 'onboarding_completed',
  PRICING_PAGE_VIEWED: 'pricing_page_viewed',
  SUBSCRIPTION_CLICKED: 'subscription_clicked',
  DASHBOARD_VIEWED: 'dashboard_viewed',
  FLASHCARD_VIEWED: 'flashcard_viewed',
  FLASHCARD_FLIPPED: 'flashcard_flipped',
  FLASHCARD_EDITED: 'flashcard_edited',
  FLASHCARD_DECK_CREATED: 'flashcard_deck_created',
  FLASHCARD_SESSION_STARTED: 'flashcard_session_started',
  FLASHCARD_SESSION_COMPLETED: 'flashcard_session_completed',
  TEST_SCORE_VIEWED: 'test_score_viewed',
  ADAPTIVE_ALGORITHM_DECISION: 'adaptive_algorithm_decision',
  // Generic error events (backwards compatibility)
  ERROR_OCCURRED: 'error_occurred',
  API_ERROR: 'api_error',
  
  // 🔥 CRITICAL: Blocks core user journeys
  QUIZ_LOAD_FAILED: 'quiz_load_failed',
  QUESTION_SUBMIT_FAILED: 'question_submit_failed',
  PAYMENT_FAILED: 'payment_failed',
  SIGNIN_FAILED: 'signin_failed',
  
  // 📚 HIGH: Feature-specific errors that impact learning
  FLASHCARD_LOAD_FAILED: 'flashcard_load_failed',
  PROGRESS_SYNC_FAILED: 'progress_sync_failed',
  CONTENT_LOAD_FAILED: 'content_load_failed',
  
  // 🚀 MEDIUM: Performance issues
  QUIZ_ERROR: 'quiz_error',
  FLASHCARD_ERROR: 'flashcard_error',
  API_TIMEOUT: 'api_timeout',
  PAGE_LOAD_TIMEOUT: 'page_load_timeout',
  MEMORY_ERROR: 'memory_error',
  
  // 🔧 LOW: Technical errors
  NETWORK_ERROR: 'network_error',
  JS_ERROR: 'js_error',
  PAGE_LOAD_TIME: 'page_load_time',
  API_RESPONSE_TIME: 'api_response_time',
  SCROLL_DEPTH: 'scroll_depth',
  TIME_ON_PAGE: 'time_on_page',
  BUTTON_CLICK: 'button_click',
  LINK_CLICK: 'link_click',
  QUIZ_TIME_SPENT: 'quiz_time_spent',
  QUESTION_TIME_SPENT: 'question_time_spent',
  ANSWER_CHANGED: 'answer_changed',
  HINT_VIEWED: 'hint_viewed',
  ONBOARDING_STEP_COMPLETED: 'onboarding_step_completed',
  ONBOARDING_STEP_VIEWED: 'onboarding_step_viewed',
  // The trial doorway (#225): onboarding as the one path into the trial week.
  ONBOARDING_DOORWAY_VIEWED: 'onboarding_doorway_viewed',
  // Licensing path (#411). Not a Google Ads key event.
  LICENSING_CARD_VIEWED: 'licensing_card_viewed',
  SUBSCRIPTION_STEP: 'subscription_step',
  FEATURE_USAGE: 'feature_usage',
  MEMORY_USAGE: 'memory_usage',
  QUIZ_STATE_CHANGED: 'quiz_state_changed',
  QUIZ_SAVE_ATTEMPTED: 'quiz_save_attempted',
  QUIZ_SAVE_FAILED: 'quiz_save_failed',
  QUESTION_VALIDATION_FAILED: 'question_validation_failed',
  QUIZ_PROGRESS_MIGRATION: 'quiz_progress_migration',
  QUIZ_PROGRESS_CORRUPTED: 'quiz_progress_corrupted',
  EXAM_DATE_UPDATED: 'exam_date_updated',
  SHARE_RESULT: 'share_result',
  QUIZ_TYPE_SELECTED: 'quiz_type_selected',
  FLASHCARD_EXPLANATION_UPDATED: 'flashcard_explanation_updated',
  ERROR: 'error',
  
  // Dashboard specific events
  DASHBOARD_INTERACTION: 'dashboard_interaction',
  QUIZ_FILTER_CHANGED: 'quiz_filter_changed',
  FLASHCARD_MARKED_COMPLETE: 'flashcard_marked_complete',
  PAGE_VIEWED: 'page_viewed',
  WEB_VITAL_RECORDED: 'web_vital_recorded',
  
  // Settings specific events
  SETTINGS_UPDATE_PROFILE: 'settings_update_profile',
  SETTINGS_MANAGE_SUBSCRIPTION: 'settings_manage_subscription',
  SETTINGS_CHANGE_PASSWORD: 'settings_change_password',
  SETTINGS_SEND_FEEDBACK: 'settings_send_feedback',
  SETTINGS_PAGE_VIEWED: 'settings_page_viewed',
  SETTINGS_USER_LOGOUT: 'settings_user_logout',
  SETTINGS_SUPPORT_CONTACT_CLICKED: 'settings_support_contact_clicked',
  SETTINGS_EMAIL_PREFERENCE_UPDATED: 'settings_email_preference_updated',
  
  // Auth Flow Events
  AUTH_STARTED: 'auth_started',
  AUTH_COMPLETED: 'auth_completed',
  AUTH_ERROR: 'auth_error',
  AUTH_METHOD_SELECTED: 'auth_method_selected',
  AUTH_RESET_PASSWORD: 'auth_reset_password',
  AUTH_EMAIL_VERIFICATION: 'auth_email_verification',
  AUTH_SESSION_STARTED: 'auth_session_started',
  AUTH_SESSION_ENDED: 'auth_session_ended',
  
  // Revenue and Subscription Events
  SUBSCRIPTION_COMPLETED: 'subscription_completed',
  SUBSCRIPTION_FAILED: 'subscription_failed',
  SUBSCRIPTION_CANCELLED: 'subscription_cancelled',
  
  // Conversion Funnel Events
  ANONYMOUS_TO_AUTH_CONVERSION: 'anonymous_to_auth_conversion',
  TRIAL_TO_PAID_CONVERSION: 'trial_to_paid_conversion',
  LANDING_PAGE_VIEWED: 'landing_page_viewed',
  TRIAL_SIGNUP_STARTED: 'trial_signup_started',
  TRIAL_SIGNUP_COMPLETED: 'trial_signup_completed',
  TRIAL_ACTIVATED: 'trial_activated',
  TRIAL_CONVERTED: 'trial_converted',
  SIGN_UP_STARTED: 'sign_up_started',
  SIGN_UP_FAILED: 'sign_up_failed',
  QUIZ_LATENCY: 'quiz_latency',
  FREE_CAP_HIT: 'free_cap_hit',
  USER_INTERACTION: 'user_interaction',
  
  // Email Attribution Events
  EMAIL_CONVERSION: 'email_conversion',
  
  // Business Metrics Events
  RETENTION_MILESTONE: 'retention_milestone',
  CUSTOMER_LTV_UPDATED: 'customer_ltv_updated',
  QUIZ_ENGAGEMENT_METRICS: 'quiz_engagement_metrics',
  
  // Question Quality Events
  QUESTION_FEEDBACK_PROVIDED: 'question_feedback_provided',
  
  // Enhanced Question Analytics Events
  QUESTION_INTERACTION_DETAILED: 'question_interaction_detailed',
  ANSWER_CHANGED_DETAILED: 'answer_changed_detailed',
  HINT_USED_DETAILED: 'hint_used_detailed',
  QUESTION_TIMING_DETAILED: 'question_timing_detailed',
  DISTRACTOR_ANALYSIS: 'distractor_analysis',
  
  // Google Ads Conversion Events
  GOOGLE_ADS_CONVERSION: 'google_ads_conversion',
  GOOGLE_ADS_ATTRIBUTION_CAPTURED: 'google_ads_attribution_captured',
  
  // Navigation Events
  NAVIGATION_CLICK: 'navigation_click',
  
  // Theme and Preferences
  THEME_PREFERENCE_CHANGED: 'theme_preference_changed',
  
  // Form Events
  FORM_ABANDONMENT: 'form_abandonment',
  FORM_FIELD_INTERACTION: 'form_field_interaction',
  FORM_VALIDATION_ERROR: 'form_validation_error',
  
  // Enhanced Error Events  
  PAGE_NOT_FOUND: 'page_not_found',
  
  // Enhanced Engagement Events
  SCROLL_DEPTH_DETAILED: 'scroll_depth_detailed',
  ENGAGEMENT_MILESTONE: 'engagement_milestone',

  // Portal Token Authentication Events
  TOKEN_AUTH_STARTED: 'token_auth_started',
  TOKEN_AUTH_SUCCESS: 'token_auth_success',
  TOKEN_AUTH_FAILED: 'token_auth_failed',
  TOKEN_AUTH_SKIPPED: 'token_auth_skipped',

  // Exam Result Events
  EXAM_RESULTS_PAGE_VIEWED: 'exam_results_page_viewed',
  EXAM_RESULT_SELECTED: 'exam_result_selected',
  EXAM_RESULT_SUBMITTED: 'exam_result_submitted',

  // Testimonial Events
  TESTIMONIAL_PAGE_VIEWED: 'testimonial_page_viewed',
  TESTIMONIAL_SUBMITTED: 'testimonial_submitted',

  // Review Prompt Events (Apple App Store / Google Business)
  REVIEW_PROMPT_SHOWN: 'review_prompt_shown',
  REVIEW_PROMPT_CLICKED: 'review_prompt_clicked',

  // Clinical Simulation Events
  SIMULATION_STARTED: 'simulation_started',
  SIMULATION_COMPLETED: 'simulation_completed',
  SIMULATION_ABANDONED: 'simulation_abandoned',
  SIM_SUGGESTED_SHOWN: 'sim_suggested_shown',

  // Funnel & performance events — fired via `trackGtagEvent` (window.gtag) because
  // they carry dynamic campaign attribution or dynamic metric names and are not a
  // fixed shape. Cataloged HERE so ANALYTICS_EVENTS stays the single canonical list
  // of everything we send to GA4; they are intentionally NOT routed through the
  // strict, per-event-typed `logAnalyticsEvent`. See feedback-classify-once-not-at-n-call-sites.
  DIAGNOSTIC_VIEWED: 'diagnostic_viewed',
  DIAGNOSTIC_RETURNING_VIEWED: 'diagnostic_returning_viewed',
  DIAGNOSTIC_STARTED: 'diagnostic_started',
  DIAGNOSTIC_QUESTION_ANSWERED: 'diagnostic_question_answered',
  DIAGNOSTIC_COMPLETED: 'diagnostic_completed',
  DIAGNOSTIC_EMAIL_CAPTURED: 'diagnostic_email_captured',
  DIAGNOSTIC_RETAKE_CLICKED: 'diagnostic_retake_clicked',
  DIAGNOSTIC_SUBSCRIBE_CLICKED: 'diagnostic_subscribe_clicked',
  DIAGNOSTIC_TRIAL_CLICKED: 'diagnostic_trial_clicked',
  DIAGNOSTIC_SCHEDULE_SELECTED: 'diagnostic_schedule_selected',
  // Sign-up road steps (utils/signupRoad): each carries entry, step, signedIn.
  DIAGNOSTIC_TURN_COMPLETED: 'diagnostic_turn_completed',
  DIAGNOSTIC_END_VIEWED: 'diagnostic_end_viewed',
  KEEP_IT_SHOWN: 'keep_it_shown',
  KEEP_IT_SUBMITTED: 'keep_it_submitted',
  KEEP_IT_SENT: 'keep_it_sent',
  KEEP_IT_PROVIDER: 'keep_it_provider',
  AIM_SHOWN: 'aim_shown',
  AIM_DONE: 'aim_done',
  OFFER_SHOWN: 'offer_shown',
  EMAIL_ANSWER_TAPPED: 'email_answer_tapped',
  EMAIL_ANSWER_COMMITTED: 'email_answer_committed',
  PASS_PLAN_DOWNLOADED: 'pass_plan_downloaded',
  // Pass Plan token → verified Firebase user (see utils/passPlanAuth). The mint
  // is the moment a lead becomes someone we can put on welcome/tips/trial mail.
  PASS_PLAN_AUTH_MINTED: 'pass_plan_auth_minted',
  PASS_PLAN_AUTH_FAILED: 'pass_plan_auth_failed',
  /** The signed-in learner clicked off /pass-plan into the product (onboarding or practice). */
  PASS_PLAN_START_CLICKED: 'pass_plan_start_clicked',
  // /exam-report — the 2026 post-exam collector. view/start/submit so the
  // collector itself is measurable (a collector nobody finishes is worthless).
  EXAM_REPORT_VIEWED: 'exam_report_viewed',
  EXAM_REPORT_STARTED: 'exam_report_started',
  EXAM_REPORT_SUBMITTED: 'exam_report_submitted',
  PAGE_LOAD_PERFORMANCE: 'page_load_performance'
} as const;

// Define strict types for analytics events and parameters
export type AnalyticsEventName = keyof typeof ANALYTICS_EVENTS;

// Change the type to use values instead of keys
export type AnalyticsEventType = typeof ANALYTICS_EVENTS[keyof typeof ANALYTICS_EVENTS];

// First, let's ensure our analytics events are properly typed
export const logAnalyticsEvent = <T extends AnalyticsEventType>(
  eventName: T,
  properties: AnalyticsEventParams[T]
) => {
  const analyticsInst = getAnalyticsInstance();
  if (!analyticsInst) {
    logger.warn('Analytics', 'Analytics not initialized - check Firebase configuration');
    return;
  }

  try {
    // Always fire analytics in production
    logEvent(analyticsInst, eventName, properties);
    
    // Secure debug logging - only in development
    logger.debug('Analytics', 'Analytics Event:', { eventName, properties });
  } catch (error) {
    logger.error('Analytics', 'Failed to log analytics event:', { error, eventName });
  }
};

/**
 * The ONE guarded `window.gtag('event', …)` sender. Funnel and performance events
 * that carry dynamic attribution or a dynamic metric name go through here instead
 * of the strict, fixed-shape `logAnalyticsEvent`. This replaces the ~5 copy-pasted
 * `if (window.gtag) window.gtag('event', …)` guards that used to live in
 * TryAQuestion, PassPlanDownload, EmailAnswerResult and webVitals — one guard, one
 * place to change. See memory feedback-classify-once-not-at-n-call-sites.
 *
 * Callers own their own internal-traffic suppression where they need it (e.g.
 * TryAQuestion's `track()` drops internal traffic before calling this).
 */
export const trackGtagEvent = (name: string, params: Record<string, unknown> = {}) => {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag('event', name, params);
  }
};

/**
 * A tap on an App Store link. Beacon transport so the hit survives the page
 * leaving for apps.apple.com.
 */
export const trackAppStoreTap = (placement: AppStorePlacement) => {
  if (isInternalTraffic()) return;
  trackGtagEvent('app_store_tap', {
    placement,
    surface: window.location.pathname,
    device_class: isMobileIOS() ? 'mobile_ios' : 'other',
    transport_type: 'beacon',
  });
};

/**
 * GA4 sends page_view only on a full page load; client-side route changes send
 * nothing, so SPA-only pages (/onboarding) undercount. The URL is origin + path
 * only: query strings can carry tokens (`t=`) and emails.
 */
export const trackSpaPageView = () => {
  if (isInternalTraffic()) return;
  trackGtagEvent('page_view', { page_location: window.location.origin + window.location.pathname });
};

// Add a test function to verify analytics is working (development only)
export const testAnalytics = () => {
  // Only run in development mode
  if (import.meta.env.NODE_ENV !== 'development') {
    logger.warn('Analytics', 'testAnalytics only available in development mode');
    return;
  }
  
  logger.debug('Analytics', 'Testing Analytics Configuration...');
  const analyticsInst = getAnalyticsInstance();
  logger.debug('Analytics', 'Analytics Instance:', analyticsInst ? 'Initialized' : 'Not Initialized');
  
  if (analyticsInst) {
    // Send multiple test events to ensure they're working
    logger.debug('Analytics', 'Sending test events...');
    
    logAnalyticsEvent(ANALYTICS_EVENTS.PAGE_VIEWED, {
      page_name: 'analytics_test',
      is_authenticated: false,
      timestamp: new Date().toISOString()
    });
    
    // Also try a simple custom event
    try {
      logEvent(analyticsInst, 'test_event', {
        test_parameter: 'test_value',
        timestamp: Date.now()
      });
      logger.debug('Analytics', 'Direct logEvent test sent');
    } catch (error) {
      logger.error('Analytics', 'Direct logEvent failed:', error);
    }
    
    logger.debug('Analytics', 'Test analytics events sent. Check Google Analytics Real-time reports.');
    logger.debug('Analytics', 'If events don\'t appear in GA, verify:');
    logger.debug('Analytics', '1. Measurement ID matches your GA4 property');
    logger.debug('Analytics', '2. Firebase project is linked to correct GA4 property');
    logger.debug('Analytics', '3. No ad blockers are interfering');
  } else {
    logger.error('Analytics', 'Analytics not initialized. Check Firebase configuration.');
  }
};

// Make test function available globally for debugging (development only)
if (typeof window !== 'undefined' && import.meta.env.NODE_ENV === 'development') {
  window.testAnalytics = testAnalytics;
}

// Add performance tracking
export const trackQuizPerformance = (quizId: number) => {
  const startTime = performance.now();
  return {
    end: () => {
      const duration = performance.now() - startTime;
      logAnalyticsEvent(ANALYTICS_EVENTS.QUIZ_TIME_SPENT, {
        quiz_id: quizId,
        duration_ms: duration
      });
      return duration;
    }
  };
};

// Add error tracking
export const trackError = (error: Error, context: {
  component?: string;
  quizId?: number;
  questionId?: number;
}) => {
  logAnalyticsEvent(ANALYTICS_EVENTS.ERROR_OCCURRED, {
    error_type: error.name,
    error_message: error.message,
    stack_trace: error.stack,
    ...context
  });
};

// Google Ads Attribution Tracking
interface GoogleAdsAttribution {
  gclid?: string;
  wbraid?: string;
  gbraid?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  timestamp: number;
  url: string;
}

// Capture Google Ads attribution parameters
export const captureGoogleAdsAttribution = (): GoogleAdsAttribution | null => {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    
    const attribution: GoogleAdsAttribution = {
      gclid: urlParams.get('gclid') || undefined,
      wbraid: urlParams.get('wbraid') || undefined,
      gbraid: urlParams.get('gbraid') || undefined,
      utm_source: urlParams.get('utm_source') || undefined,
      utm_medium: urlParams.get('utm_medium') || undefined,
      utm_campaign: urlParams.get('utm_campaign') || undefined,
      utm_term: urlParams.get('utm_term') || undefined,
      utm_content: urlParams.get('utm_content') || undefined,
      timestamp: Date.now(),
      url: window.location.href
    };
    
    // Only store if we have Google Ads parameters
    if (attribution.gclid || attribution.wbraid || attribution.gbraid || attribution.utm_source) {
      localStorage.setItem('google_ads_attribution', JSON.stringify(attribution));
      logger.debug('Analytics', 'Google Ads Attribution Captured:', attribution);
      return attribution;
    }
    
    return null;
  } catch (error) {
    logger.error('Analytics', 'Error capturing Google Ads attribution:', error);
    return null;
  }
};

// Get stored Google Ads attribution
export const getGoogleAdsAttribution = (): GoogleAdsAttribution | null => {
  try {
    const stored = localStorage.getItem('google_ads_attribution');
    if (stored) {
      const attribution = JSON.parse(stored);
      // Check if attribution is older than 30 days (Google Ads conversion window)
      const thirtyDaysAgo = Date.now() - (30 * 24 * 60 * 60 * 1000);
      if (attribution.timestamp > thirtyDaysAgo) {
        return attribution;
      } else {
        // Clean up old attribution
        localStorage.removeItem('google_ads_attribution');
      }
    }
  } catch (error) {
    logger.error('Analytics', 'Error retrieving Google Ads attribution:', error);
  }
  return null;
};

// Enhanced conversion tracking for GA4 (auto-syncs to Google Ads)
type SubscriptionCompletedEvent = AnalyticsEventParams['subscription_completed'];
type GoogleAdsConversionEvent = AnalyticsEventParams['google_ads_conversion'];

export const trackGoogleAdsConversion = (
  conversionAction: string,
  conversionValue?: number,
  data: Partial<SubscriptionCompletedEvent> = {}
) => {
  const attribution = getGoogleAdsAttribution();

  const hasSubscriptionFields = Boolean(
    data.subscription_id &&
    data.customer_id &&
    data.plan_type &&
    data.revenue_amount !== undefined &&
    data.currency &&
    data.payment_method
  );

  if (hasSubscriptionFields) {
    const payload: SubscriptionCompletedEvent = {
      subscription_id: data.subscription_id!,
      customer_id: data.customer_id!,
      plan_type: data.plan_type!,
      revenue_amount: data.revenue_amount!,
      currency: data.currency!,
      payment_method: data.payment_method!,
      conversion_source: data.conversion_source ?? attribution?.utm_source ?? 'direct',
      time_to_conversion: data.time_to_conversion,
      trial_duration: data.trial_duration,
      anonymous_quiz_results: data.anonymous_quiz_results,
      google_ads_attribution: attribution ? JSON.stringify(attribution) : undefined
    };

    // Track conversion via GA4 (will auto-sync to Google Ads)
    logAnalyticsEvent(ANALYTICS_EVENTS.SUBSCRIPTION_COMPLETED, payload);

    logger.debug('Analytics', 'GA4 Subscription Conversion Tracked (auto-syncs to Google Ads):', {
      conversionAction,
      conversionValue
    });
    return;
  }

  const fallbackPayload: GoogleAdsConversionEvent = {
    conversion_action: conversionAction,
    conversion_value: conversionValue,
    currency: data.currency,
    conversion_source: data.conversion_source ?? attribution?.utm_source ?? 'direct',
    anonymous_quiz_results: data.anonymous_quiz_results,
    time_to_conversion: data.time_to_conversion,
    trial_duration: data.trial_duration,
    google_ads_attribution: attribution ? JSON.stringify(attribution) : undefined
  };

  logAnalyticsEvent(ANALYTICS_EVENTS.GOOGLE_ADS_CONVERSION, fallbackPayload);

  logger.debug('Analytics', 'GA4 Generic Conversion Tracked:', {
    conversionAction,
    conversionValue,
    providedFields: Object.keys(data)
  });
};

// Enhanced E-commerce tracking
export const trackEcommerceEvent = (eventType: 'purchase' | 'begin_checkout' | 'add_to_cart', data: {
  transaction_id?: string;
  value: number;
  currency?: string;
  items: Array<{
    item_id: string;
    item_name: string;
    category?: string;
    quantity?: number;
    price: number;
  }>;
}) => {
  const attribution = getGoogleAdsAttribution();
  
  // Enhanced e-commerce for GA4
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    try {
      window.gtag('event', eventType, {
        transaction_id: data.transaction_id,
        value: data.value,
        currency: data.currency || 'USD',
        items: data.items,
        // Add attribution data
        campaign_source: attribution?.utm_source,
        campaign_medium: attribution?.utm_medium,
        campaign_name: attribution?.utm_campaign,
        gclid: attribution?.gclid
      });
      logger.debug('Analytics', 'E-commerce Event Tracked:', { eventType, data });
    } catch (error) {
      logger.error('Analytics', 'E-commerce tracking error:', error);
    }
  }
};

// GA4 user identity / properties — enables segmenting EVERY metric by logged-in vs
// anonymous (and "logged-in time" via GA4's built-in engagement-time metric on a
// "logged-in" audience). gtag's typed signature is narrow, so use a permissive alias
// for the `set` calls (same approach as internalTraffic.ts). No-op when GA isn't
// loaded (dev/preview).
type GtagSet = (...args: unknown[]) => void;

/** Call on auth-state change. Sets user_id + the `logged_in` user property. */
export const setGaUserIdentity = (userId: string | null): void => {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  const g = window.gtag as unknown as GtagSet;
  if (userId) {
    g('set', { user_id: userId });
    g('set', 'user_properties', { logged_in: 'true' });
  } else {
    g('set', { user_id: undefined });
    g('set', 'user_properties', { logged_in: 'false' });
  }
};

/** Set the `subscription_status` user property (call when user data loads/changes). */
export const setGaSubscriptionStatus = (status: string | null | undefined): void => {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  const g = window.gtag as unknown as GtagSet;
  g('set', 'user_properties', { subscription_status: status || 'none' });
};

// Navigation Tracking Utilities
export const trackNavigationClick = (
  elementType: 'header_link' | 'footer_link' | 'mobile_menu' | 'logo' | 'social_media' | 'breadcrumb' | 'sidebar_link' | 'bottom_tab',
  elementName: string,
  destinationPage: string,
  isAuthenticated: boolean = false,
  currentPage?: string
) => {
  logAnalyticsEvent(ANALYTICS_EVENTS.NAVIGATION_CLICK, {
    element_type: elementType,
    element_name: elementName,
    destination_page: destinationPage,
    user_authenticated: isAuthenticated,
    timestamp: Date.now(),
    current_page: currentPage || window.location.pathname
  });
};

// Theme Preference Tracking
export const trackThemeChange = (
  previousTheme: 'light' | 'dark',
  newTheme: 'light' | 'dark',
  triggerType: 'manual' | 'system' = 'manual',
  userId?: string
) => {
  logAnalyticsEvent(ANALYTICS_EVENTS.THEME_PREFERENCE_CHANGED, {
    previous_theme: previousTheme,
    new_theme: newTheme,
    user_id: userId,
    trigger_type: triggerType,
    timestamp: Date.now()
  });
};

// Form Tracking Utilities
export const trackFormFieldInteraction = (
  formType: string,
  fieldName: string,
  interactionType: 'focus' | 'blur' | 'change' | 'error' | 'clear',
  fieldValueLength?: number,
  validationStatus?: 'valid' | 'invalid' | 'pending'
) => {
  logAnalyticsEvent(ANALYTICS_EVENTS.FORM_FIELD_INTERACTION, {
    form_type: formType,
    field_name: fieldName,
    interaction_type: interactionType,
    field_value_length: fieldValueLength,
    validation_status: validationStatus,
    timestamp: Date.now()
  });
};

export const trackFormAbandonment = (
  formType: 'signin' | 'signup' | 'settings' | 'feedback' | 'profile',
  fieldsCompleted: string[],
  fieldsTotal: number,
  timeOnForm: number,
  abandonmentPoint: string
) => {
  const completionPercentage = (fieldsCompleted.length / fieldsTotal) * 100;
  
  logAnalyticsEvent(ANALYTICS_EVENTS.FORM_ABANDONMENT, {
    form_type: formType,
    fields_completed: fieldsCompleted,
    fields_total: fieldsTotal,
    completion_percentage: completionPercentage,
    time_on_form: timeOnForm,
    abandonment_point: abandonmentPoint,
    timestamp: Date.now()
  });
};

export const trackFormValidationError = (
  formType: string,
  fieldName: string,
  errorType: string,
  errorMessage: string
) => {
  logAnalyticsEvent(ANALYTICS_EVENTS.FORM_VALIDATION_ERROR, {
    form_type: formType,
    field_name: fieldName,
    error_type: errorType,
    error_message: errorMessage,
    timestamp: Date.now()
  });
};

// Enhanced Error Tracking
export const trackApiTimeout = (
  endpoint: string,
  timeoutDuration: number,
  errorMessage: string = 'API request timed out'
) => {
  logAnalyticsEvent(ANALYTICS_EVENTS.API_TIMEOUT, {
    error_message: errorMessage,
    endpoint,
    timeout_duration: timeoutDuration,
    impact_level: 'medium' as const,
    timestamp: new Date().toISOString()
  });
};

export const trackPageNotFound = (
  requestedUrl: string,
  referrer: string = document.referrer,
  isAuthenticated: boolean = false
) => {
  logAnalyticsEvent(ANALYTICS_EVENTS.PAGE_NOT_FOUND, {
    requested_url: requestedUrl,
    referrer: referrer,
    user_authenticated: isAuthenticated,
    timestamp: Date.now(),
    user_agent: navigator.userAgent
  });
};
